import type { Billing, Organisation } from './types';
import type { Contact } from '@api/contacts';
import { useTranslation } from '@hooks/useTranslation';
import { OrderActionsDropdown } from './OrderActionsDropdown';
import type { CurrentUser } from '@/react/lib/capabilities';

interface CustomerBillToCardProps {
  orderId: number;
  canGenerateDocumentsDirectly: boolean;
  contact: Contact | null;
  organisation: Organisation | null;
  /** Shared "Bill to" name + address, mirroring the order PDFs (GRAIN-2679). */
  billing: Billing;
  /** Draft events (event_id) omitted from documents — warned before generating. */
  draftEvents: string[];
  /** False when the order has no event that would render — disables generation. */
  hasPublishableEvents: boolean;
  /** Order status (lead/confirmed/cancelled) — generating a Xero invoice is confirmed-only. */
  orderStatus: string;
  /** Set once an invoice exists — swaps the menu's Generate for View + Regenerate. */
  xeroInvoiceId: string | null;
  /** Signed-in user — gates the action menu. */
  currentUser?: CurrentUser;
}

export function CustomerBillToCard({
  orderId,
  canGenerateDocumentsDirectly,
  contact,
  organisation,
  billing,
  draftEvents,
  hasPublishableEvents,
  orderStatus,
  xeroInvoiceId,
  currentUser,
}: CustomerBillToCardProps) {
  const { t } = useTranslation();
  // Held on the organisation, so an order with no organisation has nothing to
  // show. Non-positive terms stay hidden too — 0 means due on receipt, which
  // Orders::OrderPdfPresentation#payment_terms also suppresses, so this card
  // and the documents it generates never disagree.
  const paymentTermsDays = organisation?.payment_terms_in_days ?? 0;

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
      <div className="space-y-4">
        <h3 className="font-inter text-lg font-semibold leading-normal text-off-black">
          {t('customer_card.customer_title')}
        </h3>
        <div className="space-y-1">
          <p className="font-inter text-sm font-semibold leading-tight text-off-black">
            {contact?.email || '—'}
          </p>
          <p className="font-inter text-xs font-normal leading-tight text-off-black">
            {contact?.full_name || '—'} · {contact?.phone || '—'} · {contact?.organisation_name || organisation?.name || '—'}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-inter text-lg font-semibold leading-normal text-off-black">
            {t('customer_card.bill_to_title')}
          </h3>
          <div className="hidden md:block">
            <OrderActionsDropdown
              orderId={orderId}
              draftEvents={draftEvents}
              hasPublishableEvents={hasPublishableEvents}
              orderStatus={orderStatus}
              xeroInvoiceId={xeroInvoiceId}
              canGenerateDocumentsDirectly={canGenerateDocumentsDirectly}
              currentUser={currentUser}
            />
          </div>
        </div>
        <div className="space-y-1">
          <p className="font-inter text-sm font-semibold leading-tight text-off-black">
            {billing.display_name || organisation?.name || contact?.full_name || '—'}
          </p>
          <p className="font-inter text-xs font-normal leading-tight text-off-black whitespace-pre-wrap">
            {billing.address || organisation?.billing_address || '—'}
          </p>
          {paymentTermsDays > 0 && (
            <p className="font-inter text-xs font-normal leading-tight text-off-black">
              {t('customer_card.payment_terms', { count: paymentTermsDays })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
